import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn, playButtonClickSound } from "@/lib/utils"

const tisaButtonVariants = cva(
  "btn-child inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl font-medium transition-all focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-95",
  {
    variants: {
      variant: {
        default: "btn-child-primary",
        secondary: "btn-child-secondary",
        outline: "btn-child-outline",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        reward: "btn-child-reward",
        // Theme-specific variants
        "theme-blue": "bg-tisa-blue-500 text-white hover:bg-tisa-blue-600 focus-visible:ring-tisa-blue-300",
        "theme-green": "bg-tisa-green-500 text-white hover:bg-tisa-green-600 focus-visible:ring-tisa-green-300",
        "theme-yellow": "bg-tisa-yellow-500 text-tisa-yellow-900 hover:bg-tisa-yellow-600 focus-visible:ring-tisa-yellow-300",
        "theme-red": "bg-tisa-red-500 text-white hover:bg-tisa-red-600 focus-visible:ring-tisa-red-300",
        "theme-purple": "bg-tisa-purple-500 text-white hover:bg-tisa-purple-600 focus-visible:ring-tisa-purple-300",
        "theme-orange": "bg-tisa-orange-500 text-white hover:bg-tisa-orange-600 focus-visible:ring-tisa-orange-300",
      },
      size: {
        sm: "h-11 px-4 text-child-sm min-w-[88px]",
        md: "h-14 px-6 text-child-base min-w-[112px]",
        lg: "h-16 px-8 text-child-lg min-w-[128px]",
        xl: "h-20 px-10 text-child-xl min-w-[144px]",
        icon: "h-14 w-14",
      },
      roundness: {
        default: "rounded-xl",
        full: "rounded-full",
        square: "rounded-lg",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      roundness: "default",
    },
  }
)

export interface TISAButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof tisaButtonVariants> {
  asChild?: boolean
  soundEnabled?: boolean
  icon?: React.ReactNode
  iconPosition?: "left" | "right"
  isLoading?: boolean
  celebrateOnClick?: boolean
}

const TISAButton = React.forwardRef<HTMLButtonElement, TISAButtonProps>(
  ({
    className,
    variant,
    size,
    roundness,
    asChild = false,
    soundEnabled = true,
    icon,
    iconPosition = "left",
    isLoading = false,
    celebrateOnClick = false,
    onClick,
    children,
    disabled,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button"

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled || isLoading) return

      // Play click sound
      if (soundEnabled) {
        playButtonClickSound()
      }

      // Haptic feedback on mobile
      if ('vibrate' in navigator) {
        navigator.vibrate(50)
      }

      // Celebration effect for special buttons
      if (celebrateOnClick) {
        const button = e.currentTarget
        button.classList.add('animate-success-celebration')
        setTimeout(() => {
          button.classList.remove('animate-success-celebration')
        }, 1200)
      }

      onClick?.(e)
    }

    const iconElement = isLoading ? (
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
    ) : icon

    return (
      <Comp
        className={cn(tisaButtonVariants({ variant, size, roundness, className }))}
        ref={ref}
        onClick={handleClick}
        disabled={disabled || isLoading}
        {...props}
      >
        {iconElement && iconPosition === "left" && iconElement}
        {children}
        {iconElement && iconPosition === "right" && iconElement}
      </Comp>
    )
  }
)
TISAButton.displayName = "TISAButton"

export { TISAButton, tisaButtonVariants }